import { useRef, useState } from "react";
import { ChevronDown, ChevronRight, FolderPlus, GripVertical, MoreVertical, Pencil, ShieldOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { CorpusBucket, CorpusFolder } from "@/lib/workspaceCorpus";

export type FolderNode = CorpusFolder & { children: FolderNode[]; docCount: number };

export type FolderDropTarget =
  | { kind: "sibling"; parentId: string | null; index: number; bucket: CorpusBucket }
  | { kind: "child"; parentId: string; bucket: CorpusBucket };

type Props = {
  bucket: CorpusBucket;
  nodes: FolderNode[];
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  onAddSubfolder: (parent: CorpusFolder) => void;
  onRename: (folder: CorpusFolder) => void;
  onDelete: (folder: CorpusFolder) => void;
  onToggleGrounding: (folder: CorpusFolder, exclude: boolean) => void;
  onReorderFolder: (folderId: string, target: FolderDropTarget) => void;
  onMoveDocument: (documentId: string, folderId: string | null) => void;
  depth?: number;
  parentId?: string | null;
};

export const CorpusFolderAccordion = ({
  bucket,
  nodes,
  selectedFolderId,
  onSelect,
  onAddSubfolder,
  onRename,
  onDelete,
  onToggleGrounding,
  onReorderFolder,
  onMoveDocument,
  depth = 0,
  parentId = null,
}: Props) => {
  return (
    <ul className="space-y-0.5">
      {nodes.map((node, index) => (
        <FolderRow
          key={node.id}
          node={node}
          index={index}
          bucket={bucket}
          parentId={parentId}
          selectedFolderId={selectedFolderId}
          onSelect={onSelect}
          onAddSubfolder={onAddSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          onToggleGrounding={onToggleGrounding}
          onReorderFolder={onReorderFolder}
          onMoveDocument={onMoveDocument}
          depth={depth}
        />
      ))}
      {/* Tail drop zone: drop here to become the last sibling at this level. */}
      <TailDropZone
        bucket={bucket}
        parentId={parentId}
        index={nodes.length}
        depth={depth}
        onReorderFolder={onReorderFolder}
      />
    </ul>
  );
};

type RowProps = Omit<Props, "nodes" | "depth" | "parentId"> & {
  node: FolderNode;
  index: number;
  parentId: string | null;
  depth: number;
};

type Zone = "top" | "middle" | "bottom" | null;

const FOLDER_MIME = "application/x-excerptus-corpus-folder";
const DOC_MIME = "application/x-excerptus-corpus-document";

const FolderRow = ({
  node,
  index,
  bucket,
  parentId,
  selectedFolderId,
  onSelect,
  onAddSubfolder,
  onRename,
  onDelete,
  onToggleGrounding,
  onReorderFolder,
  onMoveDocument,
  depth,
}: RowProps) => {
  const [open, setOpen] = useState(true);
  const [zone, setZone] = useState<Zone>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const isSelected = selectedFolderId === node.id;
  const hasChildren = node.children.length > 0;

  const onDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(FOLDER_MIME, JSON.stringify({ id: node.id, bucket }));
    e.dataTransfer.effectAllowed = "move";
  };

  const computeZone = (e: React.DragEvent): Zone => {
    const el = rowRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const y = e.clientY - r.top;
    const h = r.height || 1;
    if (y < h * 0.25) return "top";
    if (y > h * 0.75) return "bottom";
    return "middle";
  };

  const onDragOver = (e: React.DragEvent) => {
    const hasFolder = e.dataTransfer.types.includes(FOLDER_MIME);
    const hasDoc = e.dataTransfer.types.includes(DOC_MIME);
    if (!hasFolder && !hasDoc) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hasDoc) {
      setZone("middle");
    } else {
      setZone(computeZone(e));
    }
  };

  const onDragLeave = () => setZone(null);

  const onDrop = (e: React.DragEvent) => {
    const folderPayload = e.dataTransfer.getData(FOLDER_MIME);
    const docPayload = e.dataTransfer.getData(DOC_MIME);
    setZone(null);
    if (docPayload) {
      e.preventDefault();
      e.stopPropagation();
      try {
        const { id } = JSON.parse(docPayload) as { id: string };
        if (id) onMoveDocument(id, node.id);
      } catch { /* noop */ }
      return;
    }
    if (!folderPayload) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const { id, bucket: srcBucket } = JSON.parse(folderPayload) as { id: string; bucket: CorpusBucket };
      if (id === node.id) return;
      const z = computeZone(e);
      if (z === "middle") {
        onReorderFolder(id, { kind: "child", parentId: node.id, bucket });
        setOpen(true);
      } else if (z === "top") {
        onReorderFolder(id, { kind: "sibling", parentId, index, bucket });
      } else if (z === "bottom") {
        onReorderFolder(id, { kind: "sibling", parentId, index: index + 1, bucket });
      }
      // Note: srcBucket validity is enforced by handler (cross-bucket blocked).
      void srcBucket;
    } catch { /* noop */ }
  };

  return (
    <li>
      <div
        ref={rowRef}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          "group relative flex items-center gap-1 rounded-md px-1.5 py-1 text-sm",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/60",
          zone === "middle" && "ring-1 ring-primary/60 bg-primary/5",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {zone === "top" && (
          <span aria-hidden className="pointer-events-none absolute inset-x-1 top-0 h-0.5 rounded bg-primary" />
        )}
        {zone === "bottom" && (
          <span aria-hidden className="pointer-events-none absolute inset-x-1 bottom-0 h-0.5 rounded bg-primary" />
        )}
        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {hasChildren ? (
            open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <span className="block h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className="flex-1 truncate text-left"
          title={node.name}
        >
          {node.name}
          {node.docCount > 0 && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">{node.docCount}</span>
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label="Folder actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => onAddSubfolder(node)}>
              <FolderPlus className="mr-2 h-4 w-4" /> New sub-folder
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onRename(node)}>
              <Pencil className="mr-2 h-4 w-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={node.exclude_from_grounding}
              onCheckedChange={(v) => onToggleGrounding(node, Boolean(v))}
            >
              <ShieldOff className="mr-2 h-4 w-4" /> Exclude from AI grounding
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDelete(node)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {open && hasChildren && (
        <CorpusFolderAccordion
          bucket={bucket}
          nodes={node.children}
          selectedFolderId={selectedFolderId}
          onSelect={onSelect}
          onAddSubfolder={onAddSubfolder}
          onRename={onRename}
          onDelete={onDelete}
          onToggleGrounding={onToggleGrounding}
          onReorderFolder={onReorderFolder}
          onMoveDocument={onMoveDocument}
          depth={depth + 1}
          parentId={node.id}
        />
      )}
    </li>
  );
};

const TailDropZone = ({
  bucket,
  parentId,
  index,
  depth,
  onReorderFolder,
}: {
  bucket: CorpusBucket;
  parentId: string | null;
  index: number;
  depth: number;
  onReorderFolder: (folderId: string, target: FolderDropTarget) => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <li
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(FOLDER_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        const payload = e.dataTransfer.getData(FOLDER_MIME);
        setHover(false);
        if (!payload) return;
        e.preventDefault();
        e.stopPropagation();
        try {
          const { id } = JSON.parse(payload) as { id: string };
          onReorderFolder(id, { kind: "sibling", parentId, index, bucket });
        } catch { /* noop */ }
      }}
      className={cn("h-1.5 rounded", hover && "bg-primary")}
      style={{ marginLeft: `${depth * 12 + 4}px` }}
      aria-hidden
    />
  );
};
